import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  DiagnosticV1Assessment,
  DiagnosticV1EvidenceReviewDraft,
  DiagnosticV1EvidenceReviewDecision,
  DiagnosticV1ReviewHistoryItem,
  DiagnosticV1EvidenceLink,
  DiagnosticV1FloorSnapshot,
  DiagnosticV1FloorView,
  DiagnosticV1Freshness,
} from '../types/active-reasoning-v1';
import './ActiveReasoningV1Panel.css';
import { ActiveReasoningV1FeedbackForm } from './ActiveReasoningV1FeedbackForm';
import type { DiagnosticV1FeedbackDraft } from '../types/active-reasoning-v1';

/** This is a V1-only presentation surface, mounted by the investigation page
 * exclusively after the authenticated API confirms a persisted V1 writer.
 */
export interface ActiveReasoningV1PanelProps {
  view: DiagnosticV1FloorView;
  /** Called only for a resolved source in the authenticated snapshot. */
  onOpenEvidence?: (reference: string) => void;
  /** C.18 will provide an authenticated result-capture handler. Never a work order. */
  onRecordCheckResult?: (checkId: string, assessmentVersion: number) => void;
  /** C.15 human-review API integration is a separate authenticated action. */
  onOpenHumanReview?: (assessmentId: string, assessmentVersion: number) => void;
  /** C.16 closure route must require its own explicit approval. */
  onOpenClosureReview?: (assessmentId: string, assessmentVersion: number) => void;
  onRequestReassessment?: () => void;
  onSubmitEvidenceReview?: (draft: DiagnosticV1EvidenceReviewDraft) => Promise<void>;
  onLoadEvidenceReviews?: () => Promise<DiagnosticV1ReviewHistoryItem[]>;
  onScheduleAcceptedReview?: (reviewId: string) => Promise<void>;
  /** C.18 requires an authenticated, transactionally fenced server handler. */
  onSubmitFeedback?: (feedback: DiagnosticV1FeedbackDraft) => Promise<void>;
  canContribute?: boolean;
  /** Evidence intake only: does not grant human, closure, or memory reviews. */
  canReviewEvidence?: boolean;
  canReview?: boolean;
  retryBusy?: boolean;
}

function safeTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const freshnessDescriptions: Record<DiagnosticV1Freshness, string> = {
  CURRENT: 'Assessment current for the recorded evidence snapshot.',
  UPDATING: 'New evidence is being evaluated. The assessment below is the last completed version.',
  STALE: 'New evidence may change this assessment. Review the latest updates before relying on a proposed check.',
  UNAVAILABLE: 'Reasoning is unavailable. Previously recorded evidence and the last completed assessment remain visible.',
};

const outcomeLabels = {
  NEXT_CHECK_AVAILABLE: 'One next check to consider',
  HUMAN_DECISION_REQUIRED: 'Human judgment needed',
  NO_PRACTICAL_EVIDENCE_PATH: 'No practical evidence path identified',
  OBJECTIVE_READY_FOR_CLOSURE_REVIEW: 'Ready for human closure review',
} as const;

function validateView(view: DiagnosticV1FloorView): string | null {
  const { assessment, snapshot } = view;
  if (!assessment) return null;
  if (assessment.record_type !== 'ACTIVE_REASONING_V1_ASSESSMENT' || assessment.schema_version !== '1.1') {
    return 'Unsupported diagnostic assessment version.';
  }
  if (!snapshot || ![assessment, snapshot].every((record) =>
    record.organization_id === view.organization_id &&
    record.machine_id === view.machine_id &&
    record.investigation_id === view.investigation_id &&
    record.snapshot_id === assessment.snapshot_id)) {
    return 'The assessment and evidence do not match this investigation.';
  }
  if (snapshot.review_projection &&
    (snapshot.review_projection.snapshot_id !== assessment.snapshot_id ||
      snapshot.review_projection.assessment_version !== assessment.assessment_version)) {
    return 'The human review belongs to a different assessment version.';
  }
  if (assessment.outcome === 'HUMAN_DECISION_REQUIRED' && snapshot.review_projection?.foreground &&
      snapshot.review_projection.foreground.question !== assessment.human_decision) {
    return 'The human review question does not match the approved assessment.';
  }
  if (assessment.explanations.length > 3 || assessment.assessment_version < 1) {
    return 'The assessment does not meet the bounded diagnostic contract.';
  }
  if ((assessment.outcome === 'NEXT_CHECK_AVAILABLE') !== (assessment.next_check !== null)) {
    return 'The diagnostic outcome and next check disagree.';
  }
  if (assessment.next_check && (!assessment.next_check.site_authorization_required || !assessment.next_check.basis.length)) {
    return 'The proposed check is missing required evidence or authorization safeguards.';
  }
  if (assessment.outcome === 'HUMAN_DECISION_REQUIRED' && !assessment.human_decision?.trim()) {
    return 'The required human decision has not been identified.';
  }
  return null;
}

function EvidenceLinks({
  title, links, snapshot, onOpen,
}: {
  title: string;
  links: DiagnosticV1EvidenceLink[];
  snapshot: DiagnosticV1FloorSnapshot;
  onOpen?: (reference: string) => void;
}) {
  if (!links.length) return null;
  const sourceIndex = new Map(snapshot.sources.map((source) => [source.reference, source]));
  return <div className="r42-v1-evidence-group">
    <span className="r42-v1-evidence-heading">{title}</span>
    <ul className="r42-v1-evidence-list">
      {links.map((link, index) => {
        const source = sourceIndex.get(link.reference);
        const canOpen = Boolean(source && onOpen && source.historical === link.historical);
        return <li key={`${link.reference}-${index}`}>
          {canOpen ? <button type="button" className="r42-v1-source-link" onClick={() => onOpen?.(link.reference)} aria-label={`Open source: ${source?.label}`}>
            <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> {source?.label}
          </button> : source && source.historical === link.historical ? <details className="r42-v1-disclosure r42-v1-source-preview">
            <summary>View recorded source: {source.label}</summary>
            {source.excerpt ? <blockquote>{source.excerpt}</blockquote> : <p>Text preview unavailable for this source type. Its recorded provenance is shown below; consult the investigation evidence record for the original.</p>}
            <small>Origin: {source.origin ?? 'not recorded'}; recorded: {safeTime(source.recorded_at) ?? 'time unavailable'}</small>
          </details> : <span className="r42-v1-source-unavailable">Source unavailable — evidence cannot be verified in this assessment</span>}
          {link.historical ? <span className="r42-v1-historical">Historical, not evidence of the current cause</span> : null}
          {source?.occurred_at ? <small>Occurred: {safeTime(source.occurred_at) ?? 'time not recorded'}</small> : null}
        </li>;
      })}
    </ul>
  </div>;
}

function DiagnosticAssessment({
  assessment, snapshot, freshness, view, onOpenEvidence, onRecordCheckResult,
  onOpenHumanReview, onOpenClosureReview, onSubmitFeedback, canContribute, canReview,
}: ActiveReasoningV1PanelProps & {
  assessment: DiagnosticV1Assessment;
  snapshot: DiagnosticV1FloorSnapshot;
  freshness: DiagnosticV1Freshness;
}) {
  const [captureForAssessment, setCaptureForAssessment] = useState<string | null>(null);
  const captureContainer = useRef<HTMLDivElement | null>(null);
  const captureActive = captureForAssessment === assessment.assessment_id;
  useEffect(() => {
    if (captureActive && typeof captureContainer.current?.scrollIntoView === 'function') {
      captureContainer.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [captureActive]);
  const startResultCapture = onSubmitFeedback && !onRecordCheckResult
    ? () => setCaptureForAssessment(assessment.assessment_id) : undefined;
  const isCurrentAndOpen = freshness === 'CURRENT' && !['CLOSED', 'ARCHIVED'].includes(view.investigation_status);
  const sourceIndex = new Map(snapshot.sources.map((source) => [source.reference, source]));
  const checkBasisAvailable = Boolean(assessment.next_check?.basis.length &&
    assessment.next_check.basis.some((link) => !link.historical) &&
    assessment.next_check.basis.every((link) => sourceIndex.get(link.reference)?.historical === link.historical));
  const canRecord = isCurrentAndOpen && canContribute && checkBasisAvailable &&
    Boolean(onRecordCheckResult || startResultCapture);
  const canActOnReview = isCurrentAndOpen && canReview;
  return <>
    <section className="r42-v1-primary-block" aria-labelledby="r42-v1-understanding">
      <h3 id="r42-v1-understanding">Where things stand</h3>
      <p>{assessment.understanding}</p>
      <p className="r42-v1-assessment-note">Assessment of recorded information; causal explanations are not confirmed findings.</p>
      <EvidenceLinks title="Sources for this summary" links={assessment.understanding_basis} snapshot={snapshot} onOpen={onOpenEvidence} />
    </section>

    <section className="r42-v1-primary-block" aria-labelledby="r42-v1-explanations">
      <h3 id="r42-v1-explanations">Leading explanations <span className="r42-v1-count">{assessment.explanations.length} of 3</span></h3>
      {assessment.explanations.length ? <ol className="r42-v1-explanations">
        {assessment.explanations.map((item) => <li key={item.identifier}>
          <strong>{item.text}</strong>
          <p className="r42-v1-assessment-note">Possible explanation — not a confirmed cause.</p>
          {item.limitation ? <p className="r42-v1-limitation">{item.limitation}</p> : null}
          <div className="r42-v1-evidence-columns">
            <EvidenceLinks title="Supporting reports" links={item.support} snapshot={snapshot} onOpen={onOpenEvidence} />
            <EvidenceLinks title="Opposing or conflicting reports" links={item.opposition} snapshot={snapshot} onOpen={onOpenEvidence} />
          </div>
        </li>)}
      </ol> : <p className="r42-v1-assessment-note">No leading explanation is sufficiently grounded yet.</p>}
    </section>

    <section className="r42-v1-primary-block" aria-labelledby="r42-v1-change">
      <h3 id="r42-v1-change">What changed</h3>
      {assessment.material_changes.length ? <ul className="r42-v1-simple-list">{assessment.material_changes.slice(0, 2).map((item, index) => <li key={index}>{item}</li>)}</ul> : <p className="r42-v1-assessment-note">No material change recorded in this assessment.</p>}
      {assessment.material_changes.length > 2 ? <details className="r42-v1-disclosure"><summary>Show {assessment.material_changes.length - 2} additional changes</summary><ul className="r42-v1-simple-list">{assessment.material_changes.slice(2).map((item, index) => <li key={index}>{item}</li>)}</ul></details> : null}
    </section>

    <section className="r42-v1-next" aria-labelledby="r42-v1-next-title">
      <span className="r42-v1-kicker">Suggested direction, not work authorization</span>
      <h3 id="r42-v1-next-title">{outcomeLabels[assessment.outcome]}</h3>
      {assessment.outcome === 'NEXT_CHECK_AVAILABLE' && assessment.next_check ? <>
        <p className="r42-v1-check-text">{assessment.next_check.text}</p>
        <p><strong>Why:</strong> {assessment.next_check.rationale}</p>
        <EvidenceLinks title="Basis for this suggestion" links={assessment.next_check.basis} snapshot={snapshot} onOpen={onOpenEvidence} />
        <p className="r42-v1-safety"><i className="bi bi-shield-exclamation" aria-hidden="true" /> Only qualified personnel may decide whether and how to perform work under approved site/OEM procedures. Ace does not authorize equipment interventions.</p>
        {canRecord ? <button className="btn r42-btn-primary" type="button" onClick={() => {
          if (onRecordCheckResult) onRecordCheckResult(assessment.next_check!.identifier, assessment.assessment_version);
          else startResultCapture?.();
        }}>Record check result</button> : <p className="r42-v1-assessment-note">{!isCurrentAndOpen ? 'Result actions are unavailable for stale, updating or closed investigations.' : !canContribute ? 'Your role cannot record a diagnostic result.' : !checkBasisAvailable ? 'The source basis cannot be verified in this view; use the investigation evidence workflow.' : 'Result capture is not connected yet; use the investigation evidence workflow.'}</p>}
      </> : null}
      {assessment.outcome === 'HUMAN_DECISION_REQUIRED' ? <>
        <p>{snapshot.review_projection?.foreground?.question ?? assessment.human_decision}</p>
        {canActOnReview && onOpenHumanReview ? <button className="btn r42-btn-primary" type="button" onClick={() => onOpenHumanReview(assessment.assessment_id, assessment.assessment_version)}>Open human review</button> : <p className="r42-v1-assessment-note">{!isCurrentAndOpen ? 'Review actions are unavailable until the assessment is current and the investigation is open.' : !canReview ? 'An authorized reviewer must assess this question.' : 'Review workflow is not connected yet.'}</p>}
      </> : null}
      {assessment.outcome === 'NO_PRACTICAL_EVIDENCE_PATH' ? <p>No practical evidence path is currently identified. This does not confirm a root cause or mean the problem is resolved. Preserve the limits below and request human guidance as needed.</p> : null}
      {assessment.outcome === 'OBJECTIVE_READY_FOR_CLOSURE_REVIEW' ? <>
        <p>The stated investigation objective may be ready for review. This is not approval to close the investigation or promote Machine Knowledge.</p>
        {canActOnReview && onOpenClosureReview ? <button className="btn r42-btn-primary" type="button" onClick={() => onOpenClosureReview(assessment.assessment_id, assessment.assessment_version)}>Review closure</button> : <p className="r42-v1-assessment-note">{!isCurrentAndOpen ? 'Closure review is unavailable until the assessment is current and the investigation is open.' : !canReview ? 'Only authorized personnel can review closure.' : 'The governed closure workflow is not connected yet.'}</p>}
      </> : null}
    </section>

    {snapshot.review_projection?.alternatives.length ? <details className="r42-v1-disclosure r42-v1-review-alternatives">
      <summary>{snapshot.review_projection.alternatives.length} other review items retained</summary>
      <ul className="r42-v1-simple-list">{snapshot.review_projection.alternatives.map((item) =>
        <li key={item.identifier}><strong>{item.question}</strong> <small>Review state: {item.state.toLowerCase().replaceAll('_', ' ')} — {item.why_matters}</small></li>)}</ul>
    </details> : null}

    <section className="r42-v1-primary-block" aria-labelledby="r42-v1-unknowns">
      <h3 id="r42-v1-unknowns">What remains unknown</h3>
      {assessment.material_unknowns.length || assessment.limitations.length ? <ul className="r42-v1-simple-list">{[...assessment.material_unknowns, ...assessment.limitations].map((item, index) => <li key={index}>{item}</li>)}</ul> : <p className="r42-v1-assessment-note">No material unknown has been recorded in this assessment; this does not prove every cause has been ruled out.</p>}
    </section>

    {onSubmitFeedback && isCurrentAndOpen && canContribute ?
      <div ref={captureContainer}><ActiveReasoningV1FeedbackForm assessment={assessment} snapshot={snapshot} onSubmit={onSubmitFeedback} allowCheckFeedback={Boolean(checkBasisAvailable)}
        key={`${assessment.assessment_id}:${captureActive ? "check-result" : "feedback"}`}
        initialKind={captureActive ? "CHECK_COMPLETED" : "CHALLENGE_ASSESSMENT"}
        openForResult={captureActive} /></div> : null}

    <details className="r42-v1-disclosure r42-v1-advanced">
      <summary>Completed checks, human decisions and assessment history</summary>
      <div className="r42-v1-advanced-body">
        <h4>Completed checks</h4>
        {snapshot.completed_checks.length ? <ul className="r42-v1-simple-list">{snapshot.completed_checks.map((check) => <li key={check.identifier}><strong>{check.description}</strong> — {check.result}<small>Completed: {safeTime(check.completed_at) ?? 'time unavailable'}</small><EvidenceLinks title="Result sources" links={check.sources} snapshot={snapshot} onOpen={onOpenEvidence} /></li>)}</ul> : <p>No completed checks were included in this view.</p>}
        <h4>Human decisions</h4>
        {snapshot.human_decisions.length ? <ul className="r42-v1-simple-list">{snapshot.human_decisions.map((item) => <li key={item.identifier}><strong>{item.description}</strong><small>By {item.decided_by} on {safeTime(item.decided_at) ?? 'date unavailable'}</small><EvidenceLinks title="Decision sources" links={item.sources} snapshot={snapshot} onOpen={onOpenEvidence} /></li>)}</ul> : <p>No authorized decisions were included in this view.</p>}
        <h4>Provenance</h4>
        <p>Assessment version {assessment.assessment_version}. Recorded {safeTime(assessment.created_at) ?? 'at an unknown time'}. Evidence snapshot is version-bound; raw investigation evidence remains stored separately.</p>
        {snapshot.history_notes.length ? <ul className="r42-v1-simple-list">{snapshot.history_notes.map((note, index) => <li key={index}>{note}</li>)}</ul> : null}
      </div>
    </details>
  </>;
}

// DynamoDB review-history counters may be serialized as decimal strings.
// Compare only canonical nonnegative integer revisions; never coerce arbitrary values.
function sameRevision(left: number | string, right: number): boolean {
  const normalized = typeof left === 'number' ? left :
    /^(0|[1-9]\d*)$/.test(left) ? Number(left) : NaN;
  return Number.isSafeInteger(normalized) && normalized === right;
}

function NewEvidenceReview({ view, submit, loadReviews, scheduleAcceptedReview }: {
  view: DiagnosticV1FloorView;
  submit: (draft: DiagnosticV1EvidenceReviewDraft) => Promise<void>;
  loadReviews?: () => Promise<DiagnosticV1ReviewHistoryItem[]>;
  scheduleAcceptedReview?: (reviewId: string) => Promise<void>;
}) {
  const [decision, setDecision] = useState<DiagnosticV1EvidenceReviewDecision>('REQUEST_VERIFICATION');
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [history, setHistory] = useState<DiagnosticV1ReviewHistoryItem[]>([]);
  const loadReviewsRef = useRef(loadReviews);
  loadReviewsRef.current = loadReviews;
  const [historyError, setHistoryError] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(Boolean(loadReviews));
  const [scheduling, setScheduling] = useState<string | null>(null);
  useEffect(() => {
    if (!loadReviewsRef.current) return;
    let mounted = true;
    void loadReviewsRef.current().then((items) => {
      if (mounted) { setHistory(items); setHistoryError(false); }
    }).catch(() => { if (mounted) setHistoryError(true); })
      .finally(() => { if (mounted) setHistoryBusy(false); });
    return () => { mounted = false; };
  }, [view.investigation_id]);
  const context = view.evidence_review_context;
  const alreadyAccepted = Boolean(context && history.some((item) =>
    item.decision === 'ACCEPT_FOR_REASSESSMENT' &&
    sameRevision(item.assessment_version, context.assessment_version) &&
    sameRevision(item.evidence_revision, context.evidence_revision) &&
    item.evidence_head === context.evidence_head));
  if (!context) return null;
  return <details className="r42-v1-disclosure" aria-label="Review new evidence">
    <summary>Review new evidence</summary>
    <div className="r42-v1-review-body">
      <p>Review the latest evidence before making a governed decision. This does not authorize machine work.</p>
      <section className="r42-v1-review-history" aria-label="Recorded evidence reviews">
        <h4>Recorded decisions</h4>
        {historyBusy ? <p role="status">Loading review history…</p> : null}
        {historyError ? <p role="alert">Unable to load review history. Try reloading this investigation.</p> : null}
        {!historyBusy && !historyError && !history.length ? <p>No previous reviews recorded for this investigation.</p> : null}
        {history.map((item) => <article key={item.review_id} className="r42-v1-review-entry">
          <strong>{item.decision === 'REQUEST_VERIFICATION' ? 'More verification requested' : item.decision === 'DEFER' ? 'Review deferred' : 'Accepted for reassessment'}</strong>
          <small>{safeTime(item.created_at)} · Reviewer {item.reviewer_id} · Assessment v{item.assessment_version} · Evidence revision {item.evidence_revision}</small>
          <p>{item.rationale}</p>
          <small>{item.reassessment_status === 'QUEUED_NOT_YET_ASSESSED' ? 'Reassessment queued — awaiting assessment' : item.reassessment_status === 'PENDING_NOT_DISPATCHED' ? 'Reassessment intent pending — not dispatched' : 'No reassessment requested'}</small>
          {scheduleAcceptedReview && item.decision === 'ACCEPT_FOR_REASSESSMENT' &&
            item.reassessment_status === 'PENDING_NOT_DISPATCHED' &&
            context && sameRevision(item.assessment_version, context.assessment_version) &&
            sameRevision(item.evidence_revision, context.evidence_revision) &&
            item.evidence_head === context.evidence_head ?
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={Boolean(scheduling)}
              onClick={() => {
                setScheduling(item.review_id);
                setError(null);
                void scheduleAcceptedReview(item.review_id).then(async () => {
                  setReceipt('Accepted evidence reassessment has been queued. The previous assessment remains stale until a new assessment is committed.');
                  if (loadReviews) setHistory(await loadReviews());
                }).catch(() => setError('Unable to schedule the accepted review. The decision is preserved; reload and retry scheduling without accepting the evidence again.'))
                  .finally(() => setScheduling(null));
              }}>{scheduling === item.review_id ? 'Scheduling…' : 'Start accepted reassessment'}</button> : null}
        </article>)}
      </section>
    <form className="r42-v1-review-form" onSubmit={(event) => {
      event.preventDefault();
      if (busy || receipt || rationale.trim().length < 10 || (alreadyAccepted && decision === 'ACCEPT_FOR_REASSESSMENT')) return;
      setBusy(true);
      setError(null);
      const requestId = crypto.randomUUID();
      void submit({ ...context, request_id: requestId, decision, rationale: rationale.trim() })
        .then(async () => {
          setReceipt(decision === 'ACCEPT_FOR_REASSESSMENT'
            ? 'Review recorded. Reassessment is pending, not dispatched. The old assessment remains stale.'
            : 'Review recorded. No reassessment requested. The old assessment remains stale.');
          if (loadReviews) {
            try { setHistory(await loadReviews()); setHistoryError(false); }
            catch { setHistoryError(true); }
          }
        })
        .catch((cause: unknown) => {
          const api = cause && typeof cause === 'object' ? cause as Record<string, unknown> : null;
          const status = typeof api?.status === 'number' ? api.status : null;
          setError(status === 409
            ? 'Review conflict: this evidence may already have an accepted decision, or the assessment changed. Reload to see the latest review history before trying again.'
            : status === 403
              ? 'The server declined evidence-review authorization (HTTP 403). No decision was confirmed.'
              : `Unable to record review${status ? ` (HTTP ${status})` : ''}. No decision was confirmed; check the request response before retrying.`);
        })
        .finally(() => setBusy(false));
    }}>
      <label htmlFor="r42-v1-review-decision">Decision</label>
      <select id="r42-v1-review-decision" value={decision} disabled={busy || Boolean(receipt)}
        onChange={(event) => setDecision(event.target.value as DiagnosticV1EvidenceReviewDecision)}>
        <option value="REQUEST_VERIFICATION">Request more verification</option>
        <option value="DEFER">Defer review</option>
        <option value="ACCEPT_FOR_REASSESSMENT">Accept evidence for reassessment</option>
      </select>
      {alreadyAccepted ? <p role="status">This assessment and evidence revision already have an accepted reassessment review. Do not accept the same evidence again. Use “Start accepted reassessment” above if scheduling is pending.</p> : null}
      <label htmlFor="r42-v1-review-rationale">Review rationale (at least 10 characters)</label>
      <textarea id="r42-v1-review-rationale" value={rationale} disabled={busy || Boolean(receipt)}
        onChange={(event) => setRationale(event.target.value)} maxLength={2000} required />
      <p>Accepting evidence records a pending intent; it does not immediately update this assessment.</p>
      {error ? <p role="alert">{error}</p> : null}
      {receipt ? <p role="status">{receipt}</p> : null}
      <button type="submit" className="btn btn-sm btn-outline-secondary"
        disabled={busy || Boolean(receipt) || rationale.trim().length < 10 || historyBusy || historyError || (alreadyAccepted && decision === 'ACCEPT_FOR_REASSESSMENT')}>
        {busy ? 'Recording review…' : 'Record review decision'}
      </button>
    </form>
    </div>
  </details>;
}

export function ActiveReasoningV1Panel(props: ActiveReasoningV1PanelProps) {
  const { view, onRequestReassessment, retryBusy = false } = props;
  const error = useMemo(() => validateView(view), [view]);
  const { assessment, snapshot, service } = view;
  const freshness: DiagnosticV1Freshness = assessment && service.status === 'CURRENT' &&
    (!service.current_snapshot_id || service.current_snapshot_id !== assessment.snapshot_id) ? 'STALE' : service.status;
  const canRetry = Boolean(onRequestReassessment && props.canContribute &&
    !view.evidence_review_context &&
    !['CLOSED', 'ARCHIVED'].includes(view.investigation_status) &&
    (freshness === 'STALE' || freshness === 'UNAVAILABLE'));

  return <section className="r42-investigation-section r42-v1-panel" aria-labelledby="r42-v1-title" data-freshness={freshness.toLowerCase()}>
    <header className="r42-v1-header">
      <div><span className="r42-eyebrow">Active Reasoning V1</span><h2 id="r42-v1-title">Current diagnostic picture</h2><p><strong>Investigation objective:</strong> {view.objective}</p></div>
      <span className="r42-v1-status" aria-label={`Reasoning status: ${freshness.toLowerCase()}`}>{freshness === 'CURRENT' ? 'Current' : freshness === 'UPDATING' ? 'Updating' : freshness === 'STALE' ? 'Stale' : 'Unavailable'}</span>
    </header>
    <div className={`r42-v1-freshness r42-v1-freshness--${freshness.toLowerCase()}`} role="status" aria-live="polite">
      <span>{freshnessDescriptions[freshness]}</span>
      {assessment && freshness !== 'CURRENT' ? <span>Version {assessment.assessment_version} is retained for reference, not a current work instruction.</span> : null}
      {service.detail ? <span>{service.detail}</span> : null}
      {assessment && freshness === 'STALE' && service.detail?.includes('OFFER_REVIEW') ?
        <span>Human review is required before relying on the newer reported outcome. This does not verify a physical check or restore the previous assessment as current.</span> : null}
      {props.canReviewEvidence && props.onSubmitEvidenceReview && view.evidence_review_context && freshness === 'STALE' ?
        <NewEvidenceReview key={view.evidence_review_context.evidence_head} view={view} submit={props.onSubmitEvidenceReview} loadReviews={props.onLoadEvidenceReviews} scheduleAcceptedReview={props.onScheduleAcceptedReview} /> : null}
      {freshness === 'STALE' && !view.evidence_review_context ?
        <span role="status">Review new evidence is unavailable: the server has not verified an OFFER_REVIEW decision for the newer evidence. The existing assessment remains stale; recording additional evidence does not automatically authorize review.</span> : null}
      {freshness === 'STALE' && view.evidence_review_context && !props.canReviewEvidence ?
        <span role="status">An evidence review is available, but your investigation role does not permit recording its decision.</span> : null}
      {canRetry ? <button type="button" className="btn btn-sm btn-outline-secondary" disabled={retryBusy} onClick={onRequestReassessment}>{service.detail?.includes('OFFER_REVIEW') ? 'Request governed reassessment' : 'Request reassessment'}</button> : null}
    </div>
    {error ? <div className="r42-v1-integrity" role="alert"><strong>Diagnostic assessment unavailable</strong><p>{error} Continue recording evidence in the investigation; no recommendation is displayed.</p></div> : !assessment || !snapshot ? <div className="r42-v1-empty"><strong>No completed V1 diagnostic assessment yet.</strong><p>Continue adding observations and test results. Recording evidence does not depend on AI availability.</p></div> : <DiagnosticAssessment {...props} assessment={assessment} snapshot={snapshot} freshness={freshness} />}
  </section>;
}
